import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { BookText, Library } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/feedback/toast-store'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { FormActions, FormSection } from '@/components/forms/FormSection'
import { ImageUpload } from '@/components/forms/ImageUpload'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Switch } from '@/components/ui/Switch'
import { Textarea } from '@/components/ui/Textarea'
import { categoriesQuery, useSaveBook } from '../api'
import { bookSchema, type BookFormInput, type BookFormOutput } from '../schemas'
import type { Book, BookDetail } from '../types'

const FIELDS = Object.keys(bookSchema.shape)

function defaults(b?: Book): BookFormInput {
  return {
    title: b?.title ?? '',
    subtitle: b?.subtitle ?? '',
    authors: b?.authors ?? '',
    isbn: b?.isbn ?? '',
    publisher: b?.publisher ?? '',
    publishedYear: b?.publishedYear ? String(b.publishedYear) : '',
    categoryId: b?.category ? String(b.category.id) : '',
    language: b?.language ?? '',
    shelfLocation: b?.shelfLocation ?? '',
    totalCopies: String(b?.totalCopies ?? 1),
    description: b?.description ?? '',
    active: b?.active ?? true,
    coverFileId: null,
    removeCover: false,
  }
}

interface BookFormProps {
  book?: Book
  onSaved: (saved: BookDetail) => void
  onCancel: () => void
}

export function BookForm({ book, onSaved, onCancel }: BookFormProps) {
  const { t } = useTranslation('library')
  const save = useSaveBook()
  const categories = useQuery(categoriesQuery())
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<BookFormInput, unknown, BookFormOutput>({
    resolver: zodResolver(bookSchema),
    defaultValues: defaults(book),
  })

  const onSubmit = handleSubmit(async (values) => {
    try {
      const saved = await save.mutateAsync({ ...values, id: book?.id })
      toast.success(book ? t('Book updated') : t('Book added to the catalogue'), saved.book.title)
      onSaved(saved)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  const onLoan = book?.onLoan ?? 0

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-6">
      <FormSection title={t('Book details')} icon={<BookText />}>
        <div className="sm:col-span-2">
          <Controller
            control={control}
            name="coverFileId"
            render={({ field }) => (
              <ImageUpload
                name={watch('title')}
                label={t('Cover')}
                shape="square"
                category="avatar"
                currentUrl={book?.coverUrl ?? null}
                onUploaded={(id) => {
                  field.onChange(id)
                  setValue('removeCover', id === null)
                }}
              />
            )}
          />
        </div>
        <FormField label={t('Title')} error={errors.title?.message} required className="sm:col-span-2">
          <Input {...register('title')} autoFocus={!book} />
        </FormField>
        <FormField label={t('Subtitle')} error={errors.subtitle?.message} className="sm:col-span-2">
          <Input {...register('subtitle')} />
        </FormField>
        <FormField
          label={t('Author(s)')}
          error={errors.authors?.message}
          required
          hint={t('Separate several authors with a semicolon.')}
        >
          <Input {...register('authors')} placeholder={t('e.g. Chinua Achebe')} />
        </FormField>
        <FormField
          label={t('ISBN')}
          error={errors.isbn?.message}
          hint={t('ISBN-10 or ISBN-13; hyphens are fine.')}
        >
          <Input {...register('isbn')} inputMode="numeric" className="font-mono" placeholder="978-…" />
        </FormField>
        <FormField label={t('Publisher')} error={errors.publisher?.message}>
          <Input {...register('publisher')} />
        </FormField>
        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('Year')} error={errors.publishedYear?.message}>
            <Input {...register('publishedYear')} inputMode="numeric" maxLength={4} />
          </FormField>
          <FormField label={t('Language')} error={errors.language?.message}>
            <Input {...register('language')} placeholder={t('English')} />
          </FormField>
        </div>
        <FormField label={t('Description')} error={errors.description?.message} className="sm:col-span-2">
          <Textarea rows={3} {...register('description')} />
        </FormField>
      </FormSection>

      <FormSection title={t('Shelving & copies')} icon={<Library />} columns={3}>
        <FormField
          label={t('Category')}
          error={errors.categoryId?.message}
          hint={t('Manage categories in Library settings.')}
        >
          <Select
            placeholder={t('Uncategorised')}
            options={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            {...register('categoryId')}
          />
        </FormField>
        <FormField label={t('Shelf location')} error={errors.shelfLocation?.message}>
          <Input {...register('shelfLocation')} placeholder={t('e.g. SCI-04')} />
        </FormField>
        <FormField
          label={t('Copies owned')}
          error={errors.totalCopies?.message}
          required
          hint={
            onLoan > 0
              ? t("{{count}} on loan — the total can't go below that.", { count: onLoan })
              : t('Available copies follow automatically.')
          }
        >
          <Input
            type="number"
            min={Math.max(1, onLoan)}
            max={10000}
            inputMode="numeric"
            {...register('totalCopies')}
          />
        </FormField>
        <div className="flex items-center justify-between gap-4 rounded-control border border-line p-3 sm:col-span-2 lg:col-span-3">
          <div>
            <p className="text-sm font-medium text-ink" id="book-active-label">
              {t('In circulation')}
            </p>
            <p className="text-xs text-muted">
              {t("Withdrawn books stay in the history but can't be issued.")}
            </p>
          </div>
          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Switch
                aria-labelledby="book-active-label"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
        </div>
      </FormSection>

      <FormActions>
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          {t('Cancel')}
        </Button>
        <Button type="submit" loading={isSubmitting}>
          {book ? t('Save changes') : t('Add book')}
        </Button>
      </FormActions>
    </form>
  )
}
